import React, {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { HelpCircleFilled, Users } from 'icons/untitledui';
import routesConfig from 'constants/routesConfig.json';
import { DropdownMenu } from 'shared/components/dropdown/DropdownMenu';
import NameDropdown from 'shiftNotes/components/NameDropdown';
import { Profile } from 'teamProfiles/_types';
import * as Styled from 'shiftNotes/styles/ProfileTag.style';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import colours from 'theme/atoms/colours';

interface ProfileTagButtonProps {
  label: string;
  profileId?: string;
  entityExtractionId?: string;
  entityStartPosition?: number;
  profiles: Profile[];
  noteId?: string;
  disabled?: boolean;
  onSelectProfile?: (
    profile: Profile,
    entityExtractionId: string | undefined
  ) => void;
}

export default function ProfileTagButton({
  label,
  profileId,
  entityExtractionId,
  entityStartPosition,
  profiles,
  noteId,
  disabled = false,
  onSelectProfile,
}: ProfileTagButtonProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const history = useHistory();
  const capture = usePosthogCapture();
  const hasProfile = !!profileId;

  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [isNameDropdownOpen, setIsNameDropdownOpen] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const computeAnchorPosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const menuWidth = 298;
    const margin = 8;
    const left = Math.max(
      margin,
      Math.min(rect.left, window.innerWidth - menuWidth - margin)
    );
    return { top: rect.bottom + 4, left };
  }, []);

  const openNameDropdown = useCallback(() => {
    const position = computeAnchorPosition();
    if (position) setAnchorPosition(position);
    setIsNameDropdownOpen(true);
    capture.noteProfileDropdownOpened({ has_profile: hasProfile });
  }, [computeAnchorPosition, capture, hasProfile]);

  const handleTriggerClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (disabled) return;
      if (hasProfile) {
        setIsActionMenuOpen((open) => !open);
      } else {
        openNameDropdown();
      }
    },
    [disabled, hasProfile, openNameDropdown]
  );

  const handleGoToProfile = useCallback(
    ({ close }: { close: () => void }) => {
      close();
      if (!profileId) return;
      capture.noteProfileClickedLink();
      history.push(`${routesConfig.TEAM_PAGE}/${profileId}`);
    },
    [capture, history, profileId]
  );

  const handleChangePerson = useCallback(
    ({ close }: { close: () => void }) => {
      close();
      openNameDropdown();
    },
    [openNameDropdown]
  );

  const handleNameDropdownClose = useCallback(() => {
    setIsNameDropdownOpen(false);
    setAnchorPosition(null);
  }, []);

  const handleSelectProfile = useCallback(
    (profile: Profile) => {
      capture.noteProfileSelectedOption();
      onSelectProfile?.(profile, entityExtractionId);
      handleNameDropdownClose();
    },
    [capture, onSelectProfile, entityExtractionId, handleNameDropdownClose]
  );

  const actionMenuItems = useMemo(
    () => [
      {
        label: t('profileTag.goToProfile'),
        icon: <Users.User01 size={20} />,
        onSelect: handleGoToProfile,
      },
      {
        label: t('profileTag.changePerson'),
        icon: <Users.UserEdit size={20} />,
        onSelect: handleChangePerson,
      },
    ],
    [t, handleGoToProfile, handleChangePerson]
  );

  const ariaLabel = hasProfile
    ? t('profileTag.openMenuAriaLabel', { name: label })
    : t('profileTag.openLinkAriaLabel', { name: label });

  return (
    <>
      <Styled.InlineTagWrapper>
        <DropdownMenu
          isOpen={isActionMenuOpen}
          onOpenChange={setIsActionMenuOpen}
          items={actionMenuItems}
          usePortal
          renderTrigger={() => (
            <Styled.ProfileTagTrigger
              ref={triggerRef}
              type="button"
              $isOpen={isActionMenuOpen || isNameDropdownOpen}
              aria-haspopup="menu"
              aria-expanded={isActionMenuOpen}
              aria-label={ariaLabel}
              disabled={disabled}
              onClick={handleTriggerClick}
            >
              {hasProfile ? (
                <Styled.ProfileIconUser>
                  <Users.User02 size={15} />
                </Styled.ProfileIconUser>
              ) : (
                <Styled.ProfileIcon>
                  <HelpCircleFilled size={15} color={colours.accents.blue} />
                </Styled.ProfileIcon>
              )}
              {label}
            </Styled.ProfileTagTrigger>
          )}
        />
      </Styled.InlineTagWrapper>
      {isNameDropdownOpen && anchorPosition && (
        <NameDropdown
          name={label}
          profiles={profiles}
          hasProfile={hasProfile}
          initialProfileId={profileId}
          entityStartPosition={entityStartPosition}
          entityExtractionId={entityExtractionId}
          noteId={noteId}
          onSelectProfile={handleSelectProfile}
          onClose={handleNameDropdownClose}
          hiddenTrigger
          autoOpen
          anchorPosition={anchorPosition}
          anchorRef={triggerRef}
        />
      )}
    </>
  );
}
